// PostHog product analytics — central module.
// - No-ops when REACT_APP_POSTHOG_KEY is not set (dev/staging safe to merge/deploy).
// - Initialized on ALL domains, including whitelabelled ones (whitelabel state is
//   tagged via properties, not excluded). See .kilo/plans/1788758636526-posthog-product-analytics.md

import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';

// Never track on local development (any protocol / port on localhost or 127.0.0.1)
const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

const enabled = Boolean(POSTHOG_KEY) && !isLocalhost;

// Every custom event carries a `category` property so PostHog insights can be
// filtered/broken down by lifecycle stage without manually grouping events.
const EVENT_CATEGORIES = {
  // lifecycle
  user_signed_up: 'lifecycle',
  user_logged_in: 'lifecycle',
  email_verified: 'lifecycle',
  // activation (setup milestones on the road to first value)
  activation_widget_viewed: 'activation',
  activation_step_clicked: 'activation',
  onboarding_step_completed: 'activation',
  onboarding_use_cases_selected: 'activation',
  onboarding_goals_selected: 'activation',
  onboarding_completed: 'activation',
  invite_sent: 'activation',
  sender_connect_started: 'activation',
  sender_connected: 'activation',
  leads_imported: 'activation',
  sequence_created: 'activation',
  sequence_published: 'activation',
  integration_connected: 'activation',
  // engagement (ongoing product usage)
  template_created: 'engagement',
  warmup_enabled: 'engagement',
  // payment (anything that takes money)
  upgrade_page_viewed: 'payment',
  upgrade_started: 'payment',
  upgrade_completed: 'payment',
  subscription_cancel_clicked: 'payment',
  subscription_cancel_confirmed: 'payment',
  addon_purchase_started: 'payment',
  addon_purchase_completed: 'payment',
  dfy_purchase_started: 'payment',
  dfy_purchase_completed: 'payment',
  dfy_mailbox_purchase_started: 'payment',
  dfy_mailbox_purchase_completed: 'payment',
  // churn (shrinking usage / leaving signals)
  dfy_domain_deleted: 'churn',
  dfy_mailbox_deleted: 'churn',
  // ai (BlinkGPT / Copilot observability — server-side events)
  ai_email_generated: 'ai',
  ai_copilot_message_sent: 'ai',
  ai_sequence_generated: 'ai',
};

if (enabled) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // 'history_change' so SPA navigations (react-router) count as pageviews, per PostHog docs
    capture_pageview: 'history_change',
    autocapture: true,
    // Session replay with all form inputs masked (passwords, lead emails, etc.)
    mask_all_inputs: true,
    mask_all_text: false,
    // Never block the app on analytics
    loaded: () => {},
  });
}

// Safe wrappers — call sites never need to check `enabled`

export function identifyUser(userData, accountData) {
  if (!enabled || !userData) return;
  try {
    let plan_name = null;
    let plan_duration = null;
    if (accountData) {
      plan_name = accountData?.plan;
      if (plan_name === 'ltd') plan_duration = 'lifetime';
      else if (plan_name === 'Trial') {
        plan_duration = 'trial';
        plan_name = 'trial';
      } else if (plan_name) {
        plan_name = 'paid';
        plan_duration =
          accountData?.subscription_data?.plan?.interval === 'month' ? 'monthly' : 'yearly';
      }
    }
    posthog.identify(userData.userId, {
      email: userData.email,
      name: userData.name,
      account_id: userData.account_id,
      permission: userData.permission,
      // Clickable from the PostHog person profile to log in as this user
      impersonate_link: `https://run.salesblink.io/api/public/impersonate?id=${userData.userId}&account_id=${userData.account_id}`,
      ...(plan_name ? { plan: plan_name } : {}),
      ...(plan_duration ? { plan_duration } : {}),
    });
  } catch (error) {
    // analytics must never break the app
  }
}

export function setWhitelabelProps({ whitelabelled, whitelabel_domain }) {
  if (!enabled) return;
  try {
    // register_for_current_session + super property so it lands on subsequent events
    posthog.register({
      whitelabelled: Boolean(whitelabelled),
      whitelabel_domain: whitelabel_domain || '',
    });
  } catch (error) {
    // ignore
  }
}

export function capture(event, properties) {
  if (!enabled) return;
  try {
    // category is applied last so it always reflects the taxonomy, never caller input
    posthog.capture(event, {
      ...properties,
      category: EVENT_CATEGORIES[event] || 'other',
    });
  } catch (error) {
    // ignore
  }
}

export function resetUser() {
  if (!enabled) return;
  try {
    posthog.reset();
  } catch (error) {
    // ignore
  }
}

export default posthog;
